import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/AllEngagements.graphql';
import { Query, QueryallEngagementsArgs } from '../../__generated__/globalTypes';

export const useAllEngagementsQuery = (options?: QueryHookOptions<Query, QueryallEngagementsArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryallEngagementsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useAllEngagementsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryallEngagementsArgs>
): QueryResult<Query, QueryallEngagementsArgs> & {
  getAllEngagements: LazyQueryExecFunction<Query, QueryallEngagementsArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getAllEngagements, result] = useLazyQuery<Query, QueryallEngagementsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getAllEngagements, ...result };
};

export const useAllEngagementsPromiseQuery = () => {
  const client = useApolloClient();

  const getAllEngagements = useCallback(
    (options?: Omit<QueryOptions<QueryallEngagementsArgs, Query>, 'query'>) =>
      client.query<Query, QueryallEngagementsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getAllEngagements };
};
// Suspense Query Hooks
export const useAllEngagementsSuspenseQuery = (options?: SuspenseQueryHookOptions<Query, QueryallEngagementsArgs>) => {
  const result = useSuspenseQuery<Query, QueryallEngagementsArgs>(QUERY, {
    ...options
  });

  return result;
};
